import { Box, Typography } from "@jewlr/storybook/core"
import styled from "styled-components"

import { Section } from "@jewlr/storybook/info"

const Title = styled(Typography).attrs({
  as: "h1",
  fontSize: "32px",
  fontWeight: "700",
  lineHeight: "36px",
  mb: 6,
  px: { _: 2, tablet: 0 },
})``

const SubTitle = styled(Typography).attrs({
  fontSize: "26px",
  lineHeight: "30px",
  mb: 2,
})``

const Text = styled(Typography).attrs({
  letterSpacing: "-0.02em",
})``

const HelpLink = styled(Typography).attrs({
  as: "a",
  underline: true,
})``

const TitleBox = styled(Box).attrs((props) => ({
  ...props.theme.info.accessibilityInfo.titleBox,
  ...props,
}))``

const AccessibilityPlan = () => {
  return (
    <Box
      contain
      fontSize="16px"
      letterSpacing="-0.02em"
      lineHeight="20px"
      mb="100px"
    >
      <TitleBox>
        <Title>Multi-year Accessibility Plan (AODA) &ndash; Ontario</Title>
      </TitleBox>
      <Section mb={6}>
        <Text>
          This accessibility plan outlines the strategy of{" "}
          {sl.config.site_title} to prevent and remove barriers for people with
          disabilities and comply with the requirements of the Integrated
          Accessibility Standards Regulation under the Accessibility for
          Ontarian&apos;s with Disabilities Act, 2005.
        </Text>
      </Section>
      <Section mb={6}>
        <SubTitle>Statement of Commitment</SubTitle>
        <Text mb={2}>
          {sl.config.site_title} is committed to providing an accessible
          environment for all clients, employees, job applicants, suppliers, and
          visitors who may enter our premises, access our information, or use
          our services. As an organization, we respect and comply with the
          requirements of the Accessibility for Ontarian&apos;s with
          Disabilities Act, 2005, and its associated regulations. We strive to
          provide an accessible and welcoming environment for everyone by
          identifying and removing barriers in our workplace and ensuring that
          new barriers are not created. The company ensures that persons with
          disabilities are provided with equal opportunities. We are committed
          to meeting the needs of individuals with disabilities in a timely and
          integrative manner that respects their dignity and independence.
        </Text>
        <Text mb={2}>
          Our plan shows how we will play our role in making Ontario an
          accessible province for all Ontarian&apos;s.
        </Text>
        <Text mb={2}>
          The Plan is reviewed and updated at least once every 5 years.
        </Text>
        <Text mb={2}>
          We train every person as soon as practicable after being hired and
          provide training in respect of any changes to the policies.
        </Text>
        <Text>
          We maintain records of the training provided including the dates on
          which the training was provided and the number of individuals to whom
          it was provided.
        </Text>
      </Section>
      <Section mb={6}>
        <SubTitle>Multi-year Accessibility Plan</SubTitle>
        <Text>This plan is in effect until June 1, 2026.</Text>
        <Text>
          If you have any questions or concerns about this plan or its
          initiatives, or if you want to receive a copy of the plan in a
          different accessible format, please contact the HR Department at{" "}
          <HelpLink
            as="a"
            href={`tel:${sl.config.customer_service_toll_free},309`}
            rel="nofollow"
          >
            {sl.config.customer_service_toll_free} x309
          </HelpLink>
          .
        </Text>
      </Section>

      <TitleBox>
        <Title>Integrated Accessibility Standards</Title>
      </TitleBox>

      <Section mb={6}>
        <SubTitle>General</SubTitle>
        <SubTitle>Establishment of Accessibility Policies</SubTitle>
        <Text>
          We have made a public statement of commitment and implemented an
          Accessible Customer Service Policy to ensure that people with
          disabilities are given the same opportunity to access and benefit from
          our goods and services, in the same place and in a similar way as
          other customers.
        </Text>
      </Section>

      <Section mb={6}>
        <SubTitle>Establishment of Multi Year Accessibility Plan</SubTitle>
        <Text>
          We have implemented a 5-year plan to make ourselves accessible for
          both our employees and customers.
        </Text>
        <Text>
          The plan can be provided in accessible formats upon request to the HR
          department.
        </Text>
        <Text>
          The plan will be reviewed and amended as required every 5 years.
        </Text>
      </Section>

      <Section mb={6}>
        <SubTitle>Training</SubTitle>
        <Text mb={2}>
          We are committed to training all staff and volunteers in accessible
          customer service, other Ontario&apos;s accessibility standards and
          aspects of the Ontario Human Rights Code that relate to persons with
          disabilities.
        </Text>
        <Text>In addition, we will train:</Text>
        <ol className="list-[lower-alpha]">
          <li>
            All persons who participate in developing the organization&apos;s
            policies; and
          </li>
          <li>
            All other persons who provide goods, services or facilities on
            behalf of the organization
          </li>
        </ol>
        <Text mb={2}>
          Training of our employees and volunteers on accessibility relates to
          their specific roles.
        </Text>
        <Text>Training will include:</Text>
        <ul>
          <li>
            Purpose of the Accessibility for Ontarians with Disabilities Act,
            2005 and the requirements of the Customer Service Standards
          </li>
          <li>Our policies related to the Customer Service Standards</li>
          <li>
            How to interact and communicate with people with various types of
            disabilities.
          </li>
          <li>
            How to interact with people with disabilities who use an assistive
            device or require the assistance of a guide dog or other service
            animal or the assistance of a support person.
          </li>
          <li>
            How to use the equipment or assistive devices that are available on
            our premises or that we otherwise provide that may help with the
            provision of goods and services to people with disabilities.
          </li>
          <li>
            What to do if a person with a disability is having difficulty in
            accessing our goods, services or facilities.
          </li>
        </ul>
        <Text>
          We train every person as soon as practicable after being hired and
          provide training in respect of any changes to the policies. Training
          records will be kept, including the dates when the training is
          delivered, the number of employees to whom the training was provided
          and their names.
        </Text>
      </Section>

      <Section mb={6}>
        <SubTitle>Customer Service Standards </SubTitle>
        <Text mb={2}>
          An Accessible Customer Service Policy was implemented to ensure that
          people with disabilities are given the same opportunity to access and
          benefit from our goods and services, in the same place and in a
          similar way as other customers.
        </Text>
        <Text mb={2}>
          We have developed and delivered training to all staff, volunteers, and
          individuals completing work on behalf of {sl.config.site_title} who
          may assistance to the public. All new employees or contractors are
          trained trained as part of the onboarding process.
        </Text>
        <Text>
          We have made public a process for receiving and responding to feedback
          from customers with disabilities. The feedback process includes
          multiple means of receiving feedback, including by phone and email.
        </Text>
      </Section>

      <Section mb={6}>
        <SubTitle>Information and Communication Standards</SubTitle>
        <Text mb={2}>
          Should we launch a new internet website or significantly refresh an
          existing internet website, we will ensure that the website and all
          content on that website that has been posted is accessible. We are
          continually working to improve and we will ensure that the website and
          all content on that website conforms to the Web Content Accessibility
          Guidelines 2.0, Level A.
        </Text>
        <Text>
          In order to ensure all of our internet websites, and all content
          posted on those websites conforms to the WCAG 2.0 Level AA we will:
        </Text>
        <ul>
          <li>Audit all websites and content for Level AA compliance.</li>
          <li>
            Implement the necessary changes to bring the websites and web
            content into conformance with applicable Level AA requirements.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Feedback</SubTitle>
        <Text>
          We will take the following steps to make sure existing feedback
          processes are accessible to people with disabilities upon request:
        </Text>
        <ul>
          <li>
            A process for receiving and responding to accessible feedback
            requests has been developed and communicated to all relevant
            employees, contractors and volunteers. Currently{" "}
            {sl.config.site_title} can facilitate requests via phone and email.
            Contact us at{" "}
            <HelpLink
              as="a"
              href={`tel:${sl.config.customer_service_toll_free},309`}
              rel="nofollow"
            >
              {sl.config.customer_service_toll_free} x309
            </HelpLink>{" "}
            or email us at{" "}
            <HelpLink
              href={`mailto:${sl.config.customer_service_email}`}
              rel="nofollow"
            >
              {sl.config.customer_service_email}
            </HelpLink>
          </li>
          <li>
            As feedback may be received by various departments and personnel at
            {sl.config.site_title}, including customer service, training on how
            to receive and respond to accessible feedback requests has been
            developed and delivered.
          </li>
          <li>
            Receiving and responding to feedback is included in all new 1hire
            orientations where the position frequently receives and responds to
            requests.
          </li>
          <li>
            All other employees, contractors and volunteers will be notified as
            to the internal party to whom they should direct any accessible
            feedback requests.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Accessible Formats and Communication Supports</SubTitle>
        <Text>Planned Action/Ongoing and Action Taken:</Text>
        <Text>
          We will ensure that all of our publicly available information is made
          accessible upon request. Where a request for an accessible format or
          for communication support is received, we will:
        </Text>
        <ul>
          <li>
            Consult with the individual making the request to determine their
            accessibility needs and what would be suitable format or support.
          </li>
          <li>Provide the requested information in a timely manner.</li>
          <li>Provide the information at regular cost (if any).</li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Emergency Information</SubTitle>
        <Text>
          Public safety information that is prepared by {sl.config.site_title}{" "}
          and made available to the public will be made available in an
          accessible format or with appropriate communication supports, as soon
          as practicable, upon request.
        </Text>
      </Section>
      <TitleBox>
        <Title>Employment Standards</Title>
      </TitleBox>
      <Section mb={6}>
        <SubTitle>Workplace Emergency Response Information</SubTitle>
        <Text>
          In situations where we are aware that an employee has a disability and
          that there is a need for accommodation, individualized workplace
          emergency response information are provided to the employee as soon as
          practicable if such information is necessary given the nature of the
          employee&apos;s disability. These individualized emergency response
          plans are:
        </Text>
        <ul>
          <li>
            Communicated to the employee&apos;s respective manager and HR, where
            the employee&apos;s consent has been obtained and on an as needed
            basis.
          </li>
          <li>
            Reviewed and assessed on an ongoing and regular basis to ensure that
            accessibility issues are addressed.
          </li>
        </ul>
      </Section>
      <Section mb={6}>
        <SubTitle>Recruitment General</SubTitle>
        <Text>
          Employees and the public will be notified of the availability of
          accommodation for applicants with disabilities in the recruitment
          process. This will include:
        </Text>
        <ul>
          <li>
            A review and modification of existing recruitment policies,
            procedures and processes.
          </li>
          <li>
            Specifying on job postings that accommodation is available for
            applicants with disabilities.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Recruitment, Assessment and Selection</SubTitle>
        <Text>
          Job applicants will be notified when they are individually selected to
          participate in an assessment or selection process that accommodations
          are available upon request in relation to the materials and processes
          to be used in the assessment or selection process. This will include:
        </Text>
        <ul>
          <li>
            A review and, as necessary, modification of existing recruitment
            policies, procedures and processes.
          </li>
          <li>
            If a selected applicant requests an accommodation, consult with the
            applicant and arrange for provision of suitable accommodation in a
            manner that takes into account the applicant&apos;s accessibility
            needs.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Notice to Successful Applicants</SubTitle>
        <Text>
          Action Taken:
          <br />
          When making offers of employment, we will notify the successful
          applicant of our policies for accommodating employees with
          disabilities. This will include:
        </Text>
        <ul>
          <li>
            A review and, as necessary, modification of existing recruitment
            policies, procedures and processes.
          </li>
          <li>
            Inclusion of notification of {sl.config.site_title} policy on
            accommodating employees with disabilities in employment offer
            letters.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>Informing Employees of Supports</SubTitle>
        <Text>
          All employees will be informed of policies that support employees with
          disabilities, including, but not limited to policies on the provision
          of job accommodations that take into account an employee&apos;s
          accessibility needs due to a disability. This will include:
        </Text>
        <ul>
          <li>
            Keeping employees up to date on changes to existing policies on job
            accommodations with respect to disability.
          </li>
          <li>
            Providing information under this section as soon as practicable
            after the new employee begins employment.
          </li>
          <li>
            Informing current employees and new hires of {sl.config.site_title}{" "}
            policies on policies on supporting employees with disabilities.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>
          Accessible Formats and Communication Supports for Employees
        </SubTitle>
        <Text>
          Where an employee with disability requests, we will provide or arrange
          for provision of suitable accessible formats and communication
          supports for:
        </Text>
        <ul>
          <li>
            Information that is generally available to employees in the
            workplace.
          </li>
          <li>
            Information that is needed in order to perform the employee&apos;s
            job.
          </li>
        </ul>
        <Text>
          In order to meet this obligation, the requesting employee will be
          consulted to determine the suitability of an accessible format or
          communication support.
        </Text>
      </Section>

      <Section mb={6}>
        <SubTitle>
          Documented Individual Accommodation Plans / Return to Work Process
        </SubTitle>
        <Text mb={2}>
          Our existing processes include steps that we take to accommodate an
          employee with a disability and to facilitate an employee&apos;s return
          to work after absenteeism due to disability.
        </Text>
        <Text mb={2}>
          We will review and assess the existing processes to ensure that they
          include a method for the development of documented individual
          accommodation plans for employees with a disability, if such plans are
          required.
        </Text>
        <Text>
          We will ensure that the process for the development of documented
          individual accommodation plans includes the following elements, in
          accordance with the provisions of the IASR:
        </Text>
        <ul>
          <li>
            The manner in which the employee requesting accommodation can
            participate in the development of the plan.
          </li>
          <li>
            The means by which the employee is assessed on an individual basis.
          </li>
          <li>
            The manner in which we can request an evaluation by an outside
            medical or other expert to assist us in determining if and how
            accommodation can be achieved.
          </li>
          <li>
            The steps taken to protect the privacy of the employee&apos;s
            personal information.
          </li>
          <li>
            The frequency in which individual accommodation plans will be
            reviewed and updated and the manner in which this will be done.
          </li>
          <li>
            If an individual accommodation plan is denied, the manner in which
            the reasons for the denial will be provided to the employee.
          </li>
          <li>
            The means of providing the individual accommodation plan in a format
            that takes into account the employee&apos;s accessibility needs due
            to a disability.
          </li>
        </ul>
        <Text>
          For Individual accommodation plans that are established, we will
          ensure that they include:
        </Text>
        <ul>
          <li>Individualized workplace emergency response information.</li>
        </ul>
        <Text>
          Any information regarding accessible formats and communication
          supports that have been provided for or arranged to provide employee
          will include:
        </Text>
        <ul>
          <li>
            Information that is needed in order to perform the employee&apos;s
            job.
          </li>
          <li>
            Information that is generally available to employees in the
            workplace.
          </li>
          <li>
            Any other accommodation that is to be provided to the employee.
          </li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>
          Performance Management, Career Development and Redeployment
        </SubTitle>
        <Text>
          We will take into account the accessibility needs of employees with
          disabilities, as well as individual accommodation plans:
        </Text>
        <ul>
          <li>
            When using our performance management process in respect of
            employees with disabilities.
          </li>
          <li>
            When providing career development and advancement to our employees
            with disabilities.
          </li>
          <li>When redeploying employees with disabilities.</li>
        </ul>
      </Section>

      <Section mb={6}>
        <SubTitle>
          Design of Public Spaces Standards (Accessibility Standards for the
          Built Environment)
        </SubTitle>
        <Text>
          {sl.config.site_title} will incorporate accessibility into the
          Company&apos;s public spaces that are newly constructed or redeveloped
          on and after January 1, 2017. We will ensure that we follow the
          requirements stated under the Design of Public Spaces Standards
          (Accessibility Standards for the Built Environment).
        </Text>
      </Section>
    </Box>
  )
}

export default AccessibilityPlan
